import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { FilterMatchMode } from 'primereact/api';
import { RouteComponentProps } from '../../models/RouteComponent';
import { getChecklistTasks, sendChecklistReminder } from '../../service/checklistService';

interface ChecklistTask {
  id: string;
  template_id: string;
  checklist_name: string;
  site_id: string;
  site_name: string;
  appointment_name: string;
  assigned_person: string;
  frequency: string;
  due_date: string;
  status: 'Not Started' | 'Due Soon' | 'Due Today' | 'In Progress' | 'Overdue' | 'Completed';
}

interface ChecklistTaskTrackerProps extends RouteComponentProps {}

const ChecklistTaskTracker: React.FC<ChecklistTaskTrackerProps> = ({
  session,
  globalLoader
}) => {
  const [tasks, setTasks] = useState<ChecklistTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    site_name: { value: null, matchMode: FilterMatchMode.EQUALS },
    appointment_name: { value: null, matchMode: FilterMatchMode.EQUALS },
    assigned_person: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    due_date: { value: null, matchMode: FilterMatchMode.DATE_IS }
  });
  
  const toast = useRef<Toast>(null);

  // Filter options
  const statusOptions = [
    { label: 'All', value: null },
    { label: 'Not Started', value: 'Not Started' },
    { label: 'Due Soon', value: 'Due Soon' },
    { label: 'Due Today', value: 'Due Today' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Overdue', value: 'Overdue' },
    { label: 'Completed', value: 'Completed' }
  ];

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!session?.client_id) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Client information not available'
        });
        return;
      }

      const response = await getChecklistTasks({
        activeClientId: session.client_id,
        limit: 100
      });
      
      if (response && response.content && response.content.tasks) {
        // Convert due_date strings to Date objects and map field names for display
        const tasksWithDates = response.content.tasks.map((task: any) => ({
          ...task,
          due_date_obj: new Date(task.due_date),
          // Map API field names to component field names
          checklist_name: task.template_name,
          assigned_person: task.assignee_name,
          frequency: task.trigger_type,
          appointment_name: task.appointment_name || 'N/A' // Handle missing appointment data
        }));
        setTasks(tasksWithDates);
      }
    } catch (error) {
      console.error('Error loading checklist tasks:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load checklist tasks'
      });
    } finally {
      setLoading(false);
    }
  }, [session?.client_id]);

  useEffect(() => {
    if (session?.client_id) {
      loadTasks();
    }
  }, [session?.client_id, loadTasks]);

  const sendReminder = async (task: ChecklistTask) => {
    try {
      await sendChecklistReminder({ 
        taskId: task.id, 
        assignedPerson: task.assigned_person 
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Reminder Sent',
        detail: `Reminder sent to ${task.assigned_person} for ${task.checklist_name}`
      });
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to send reminder'
      });
    }
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value as any;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilters = () => {
    setGlobalFilterValue('');
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      site_name: { value: null, matchMode: FilterMatchMode.EQUALS },
      appointment_name: { value: null, matchMode: FilterMatchMode.EQUALS },
      assigned_person: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
      due_date: { value: null, matchMode: FilterMatchMode.DATE_IS }
    });
  };

  // Column templates
  const statusBodyTemplate = (rowData: ChecklistTask) => {
    const getSeverity = (status: string) => {
      switch (status) {
        case 'Completed':
          return 'success';
        case 'In Progress':
        case 'Due Soon':
          return 'info';
        case 'Overdue':
          return 'danger';
        case 'Not Started':
        case 'Due Today':
          return 'warning';
        default:
          return null;
      }
    };

    return (
      <Tag 
        value={rowData.status} 
        severity={getSeverity(rowData.status)} 
      />
    );
  };

  const dueDateBodyTemplate = (rowData: ChecklistTask) => {
    const dueDate = new Date(rowData.due_date);
    return dueDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const actionsBodyTemplate = (rowData: ChecklistTask) => {
    return (
      <Button
        icon="pi pi-send"
        label="Send Reminder"
        size="small"
        text
        onClick={() => sendReminder(rowData)}
        disabled={rowData.status === 'Completed' || rowData.assigned_person === 'Not Assigned'}
        tooltip="Send reminder to assigned person"
      />
    );
  };

  // Filter templates
  const siteFilterTemplate = (options: any) => {
    const uniqueSites = Array.from(new Set(tasks.map(task => task.site_name)));
    const siteOptions = [
      { label: 'All Sites', value: null },
      ...uniqueSites.map(site => ({ label: site, value: site }))
    ];

    return (
      <Dropdown
        value={options.value}
        options={siteOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder="Select Site"
        className="p-column-filter"
        showClear
      />
    );
  };

  const appointmentFilterTemplate = (options: any) => {
    const uniqueAppointments = Array.from(new Set(tasks.map(task => task.appointment_name)));
    const appointmentOptions = [
      { label: 'All Appointments', value: null },
      ...uniqueAppointments.map(appointment => ({ label: appointment, value: appointment }))
    ];

    return (
      <Dropdown
        value={options.value}
        options={appointmentOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder="Select Appointment"
        className="p-column-filter"
        showClear
        filter
        filterPlaceholder="Search"
      />
    );
  };

  const statusFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder="Select Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const assignedPersonFilterTemplate = (options: any) => {
    return (
      <InputText
        value={options.value || ''}
        onChange={(e) => options.filterCallback(e.target.value)}
        placeholder="Search assigned person"
        className="p-column-filter"
      />
    );
  };

  const dueDateFilterTemplate = (options: any) => {
    return (
      <Calendar
        value={options.value}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder="Select date"
        className="p-column-filter"
        showIcon
      />
    );
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between align-items-center">
        <h2 className="m-0">Checklist Task Tracker</h2>
        <div className="flex gap-2">
          <Button
            type="button"
            icon="pi pi-refresh"
            label="Refresh"
            outlined
            onClick={loadTasks}
          />
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            outlined
            onClick={clearFilters}
          />
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Global Search"
            />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <Toast ref={toast} />
      
      <DataTable
        value={tasks}
        paginator
        rows={25}
        loading={loading}
        dataKey="id"
        filters={filters}
        globalFilterFields={['checklist_name', 'site_name', 'appointment_name', 'assigned_person']}
        header={renderHeader()}
        emptyMessage="No checklist tasks found"
        responsiveLayout="scroll"
        className="mt-3"
      >
        <Column
          field="checklist_name"
          header="Checklist Name"
          sortable
          style={{ minWidth: '200px' }}
        />
        
        <Column
          field="site_name"
          header="Site"
          sortable
          filter
          filterElement={siteFilterTemplate}
          style={{ minWidth: '150px' }}
        />
        
        <Column
          field="appointment_name"
          header="Appointment"
          sortable
          filter
          filterElement={appointmentFilterTemplate}
          style={{ minWidth: '150px' }}
        />
        
        <Column
          field="assigned_person"
          header="Assigned Person"
          sortable
          filter
          filterElement={assignedPersonFilterTemplate}
          style={{ minWidth: '150px' }}
        />
        
        <Column
          field="frequency"
          header="Frequency"
          sortable
          style={{ minWidth: '100px' }}
        />
        
        <Column
          field="due_date"
          header="Due Date"
          sortable
          filter
          filterElement={dueDateFilterTemplate}
          body={dueDateBodyTemplate}
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="status"
          header="Status"
          sortable
          filter
          filterElement={statusFilterTemplate}
          body={statusBodyTemplate}
          style={{ minWidth: '120px' }}
        />
        
        <Column
          header="Actions"
          body={actionsBodyTemplate}
          style={{ minWidth: '150px' }}
        />
      </DataTable>
    </div>
  );
};

export default ChecklistTaskTracker;
